import { Injectable } from '@angular/core';
import { Observable, Subject, tap } from 'rxjs';
import { HttpService } from './http.service';
import { config } from '../../config/appConfig';
import { IResponse } from '../models/admin';
import { IFetchHistoryRequest, IHistoryPagedResult } from '../models/history';
import {
  ICreateOrganizationOrderRequest,
  IFetchOrganizationsRequest,
  IMyOrganizationResponse,
  IOrganization,
  IOrganizationOrder,
  IOrganizationPagedResult,
  IOrganizationPaymentLink,
  IOrganizationPlan,
  IOrganizationSubscription,
  IOrganizationSubscriptionUsers,
  ISaveOrganizationRequest,
  IUpdateOrganizationUsersRequest,
  IUpdateOrganizationUsersResponse,
} from '../models/organization';

export enum SalesEndPoints {
  organizations = '/organizations',
}

/** The server's cap on pageSize for the organizations list. */
export const ORGANIZATIONS_PAGE_SIZE = 100;

@Injectable({
  providedIn: 'root',
})
export class SalesService {
  httpService: HttpService = new HttpService(config.userServiceURL);

  private readonly organizationChangedSubject = new Subject<string>();

  /**
   * The id of an organization whose data - and so whose history - has just
   * changed through this service. Screens showing that organization (its
   * History tab, the all-organizations history) refresh on it instead of
   * waiting for a reload.
   */
  readonly organizationChanged$ =
    this.organizationChangedSubject.asObservable();

  /** Announces the change once the call succeeds; a failed call changed nothing. */
  private announce<T>(
    organizationId: (response: T) => string | null | undefined,
  ) {
    return tap<T>((response) => {
      const id = organizationId(response);
      if (id) this.organizationChangedSubject.next(id);
    });
  }

  // Get Organizations (one page; PageNumber is 0-based)
  getOrganizations(
    request: IFetchOrganizationsRequest,
  ): Observable<IOrganizationPagedResult> {
    const params = {
      pageNumber: request.PageNumber,
      pageSize: request.PageSize,
      ...(request.Search && { search: request.Search }),
    };
    return this.httpService.get(SalesEndPoints.organizations, params);
  }

  /**
   * Every organization, followed page by page.
   *
   * The endpoint caps pageSize, so asking for one big page is refused rather
   * than truncated. Following TotalPages instead means the cap can change on
   * the server without this breaking. Same approach as getAllMembers.
   */
  getAllOrganizations(): Observable<IOrganization[]> {
    return new Observable<IOrganization[]>((observer) => {
      const collected: IOrganization[] = [];
      let cancelled = false;

      const fetchPage = (pageNumber: number): void => {
        this.getOrganizations({
          PageNumber: pageNumber,
          PageSize: ORGANIZATIONS_PAGE_SIZE,
        }).subscribe({
          next: (response) => {
            if (cancelled) return;
            collected.push(...(response.Value ?? []));

            // Pages are 0-based, so the last one is TotalPages - 1.
            const totalPages = response.TotalPages ?? 0;
            if (pageNumber + 1 < totalPages) {
              fetchPage(pageNumber + 1);
            } else {
              observer.next(collected);
              observer.complete();
            }
          },
          error: (error) => observer.error(error),
        });
      };

      fetchPage(0);

      return () => {
        cancelled = true;
      };
    });
  }

  // Get Organization
  getOrganization(
    organizationId: string,
  ): Observable<IResponse<IOrganization>> {
    return this.httpService.get(
      `${SalesEndPoints.organizations}/${organizationId}`,
    );
  }

  // Create Organization
  createOrganization(
    request: ISaveOrganizationRequest,
  ): Observable<IResponse<IOrganization>> {
    return this.httpService
      .post(SalesEndPoints.organizations, request)
      .pipe(
        this.announce<IResponse<IOrganization>>(
          (r) => r?.Value?.OrganizationId,
        ),
      );
  }

  // Update Organization
  updateOrganization(
    organizationId: string,
    request: ISaveOrganizationRequest,
  ): Observable<IResponse<IOrganization>> {
    return this.httpService
      .put(`${SalesEndPoints.organizations}/${organizationId}`, request)
      .pipe(this.announce<IResponse<IOrganization>>(() => organizationId));
  }

  // Delete Organization (soft delete on the server)
  deleteOrganization(organizationId: string): Observable<IResponse<boolean>> {
    return this.httpService
      .delete(`${SalesEndPoints.organizations}/${organizationId}`)
      .pipe(this.announce<IResponse<boolean>>(() => organizationId));
  }

  /** No new orders, links or users, and its subscriptions are suspended until enabled. */
  disableOrganization(
    organizationId: string,
  ): Observable<IResponse<IOrganization>> {
    return this.httpService
      .post(`${SalesEndPoints.organizations}/${organizationId}/disable`)
      .pipe(this.announce<IResponse<IOrganization>>(() => organizationId));
  }

  enableOrganization(
    organizationId: string,
  ): Observable<IResponse<IOrganization>> {
    return this.httpService
      .post(`${SalesEndPoints.organizations}/${organizationId}/enable`)
      .pipe(this.announce<IResponse<IOrganization>>(() => organizationId));
  }

  // ------------------------------------------------------------------
  // Orders and subscriptions
  // ------------------------------------------------------------------

  // Plans that can be sold to this organization (active, one-time), priced per
  // user in the organization's country
  getOrganizationPlans(
    organizationId: string,
  ): Observable<IResponse<IOrganizationPlan[]>> {
    return this.httpService.get(
      `${SalesEndPoints.organizations}/${organizationId}/plans`,
    );
  }

  getOrganizationOrders(
    organizationId: string,
  ): Observable<IResponse<IOrganizationOrder[]>> {
    return this.httpService.get(
      `${SalesEndPoints.organizations}/${organizationId}/orders`,
    );
  }

  createOrganizationOrder(
    organizationId: string,
    request: ICreateOrganizationOrderRequest,
  ): Observable<IResponse<IOrganizationOrder>> {
    return this.httpService
      .post(`${SalesEndPoints.organizations}/${organizationId}/orders`, request)
      .pipe(this.announce<IResponse<IOrganizationOrder>>(() => organizationId));
  }

  /** Changes an order's plan, users, discount or notes - only before its payment link. */
  updateOrganizationOrder(
    organizationId: string,
    orderId: string,
    request: ICreateOrganizationOrderRequest,
  ): Observable<IResponse<IOrganizationOrder>> {
    return this.httpService
      .put(
        `${SalesEndPoints.organizations}/${organizationId}/orders/${orderId}`,
        request,
      )
      .pipe(this.announce<IResponse<IOrganizationOrder>>(() => organizationId));
  }

  /** Checks the payment with Razorpay; a paid order is fulfilled (which writes history). */
  refreshOrganizationOrder(
    organizationId: string,
    orderId: string,
  ): Observable<IResponse<IOrganizationOrder>> {
    return this.httpService
      .get(
        `${SalesEndPoints.organizations}/${organizationId}/orders/${orderId}`,
      )
      .pipe(this.announce<IResponse<IOrganizationOrder>>(() => organizationId));
  }

  /** Returns the order's payment link, generating one if there is none or it expired. */
  getOrganizationPaymentLink(
    organizationId: string,
    orderId: string,
  ): Observable<IResponse<IOrganizationPaymentLink>> {
    return this.httpService
      .post(
        `${SalesEndPoints.organizations}/${organizationId}/orders/${orderId}/payment-link`,
      )
      .pipe(
        this.announce<IResponse<IOrganizationPaymentLink>>(
          () => organizationId,
        ),
      );
  }

  cancelOrganizationOrder(
    organizationId: string,
    orderId: string,
  ): Observable<IResponse<IOrganizationOrder>> {
    return this.httpService
      .post(
        `${SalesEndPoints.organizations}/${organizationId}/orders/${orderId}/cancel`,
      )
      .pipe(this.announce<IResponse<IOrganizationOrder>>(() => organizationId));
  }

  // History newest first - one organization's (its own changes, orders, payments and users),
  // or every organization's when organizationId is null. Each entry's ScopeLabel is its organization's name.
  getOrganizationHistory(
    organizationId: string | null,
    request: IFetchHistoryRequest,
  ): Observable<IHistoryPagedResult> {
    const params = {
      pageNumber: request.PageNumber,
      pageSize: request.PageSize,
      ...(request.EntityName && { entityName: request.EntityName }),
    };
    const path = organizationId
      ? `${SalesEndPoints.organizations}/${organizationId}/history`
      : `${SalesEndPoints.organizations}/history`;
    return this.httpService.get(path, params);
  }

  // The signed-in user's subscription when an organization provides it (Value is null otherwise)
  getMyOrganizationSubscription(): Observable<
    IResponse<IMyOrganizationResponse>
  > {
    return this.httpService.get(
      `${SalesEndPoints.organizations}/me/subscription`,
    );
  }

  getOrganizationSubscriptions(
    organizationId: string,
  ): Observable<IResponse<IOrganizationSubscription[]>> {
    return this.httpService.get(
      `${SalesEndPoints.organizations}/${organizationId}/subscriptions`,
    );
  }

  /** Its users lose access until it is enabled; auto-pay keeps charging. Returns the subscriptions. */
  disableOrganizationSubscription(
    organizationId: string,
    subscriptionId: string,
  ): Observable<IResponse<IOrganizationSubscription[]>> {
    return this.httpService
      .post(
        `${SalesEndPoints.organizations}/${organizationId}/subscriptions/${subscriptionId}/disable`,
      )
      .pipe(
        this.announce<IResponse<IOrganizationSubscription[]>>(
          () => organizationId,
        ),
      );
  }

  enableOrganizationSubscription(
    organizationId: string,
    subscriptionId: string,
  ): Observable<IResponse<IOrganizationSubscription[]>> {
    return this.httpService
      .post(
        `${SalesEndPoints.organizations}/${organizationId}/subscriptions/${subscriptionId}/enable`,
      )
      .pipe(
        this.announce<IResponse<IOrganizationSubscription[]>>(
          () => organizationId,
        ),
      );
  }

  // Users on one of the organization's subscriptions - active first, then removed
  getSubscriptionUsers(
    organizationId: string,
    subscriptionId: string,
  ): Observable<IResponse<IOrganizationSubscriptionUsers>> {
    return this.httpService.get(
      `${SalesEndPoints.organizations}/${organizationId}/subscriptions/${subscriptionId}/users`,
    );
  }

  /**
   * Adds and removes users together, all or nothing - adding and removing in
   * one call swaps them. When an email is rejected the response says why and
   * nothing is saved (Saved = false).
   */
  updateSubscriptionUsers(
    organizationId: string,
    subscriptionId: string,
    request: IUpdateOrganizationUsersRequest,
  ): Observable<IResponse<IUpdateOrganizationUsersResponse>> {
    return (
      this.httpService
        .post(
          `${SalesEndPoints.organizations}/${organizationId}/subscriptions/${subscriptionId}/users`,
          request,
        )
        // Rejected emails mean nothing was saved.
        .pipe(
          this.announce<IResponse<IUpdateOrganizationUsersResponse>>((r) =>
            r?.Value?.Saved ? organizationId : null,
          ),
        )
    );
  }
}
